import { Box, Chip, Table, TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';

import { TableSurface } from '../../../../components/UI/Surface';

export const TableWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.palette.tokens.space[3],
}));

export const FiltersRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.palette.tokens.space[2],
  flexWrap: 'wrap',
  alignItems: 'center',
}));

export const FiltersLabel = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.disabled,
  marginRight: '4px',
}));

/** Filter chip (guide 7.5): pill, surface + hairline; active = tint + text-primary. */
export const FilterChip = styled(Chip)(({ theme }) => ({
  height: '28px',
  cursor: 'pointer',
  '& .MuiChip-label': {
    padding: '0 10px',
  },
  '&.MuiChip-filledPrimary': {
    backgroundColor: theme.palette.tokens.color.actionTint,
    color: theme.palette.text.primary,
    '&:hover': { backgroundColor: theme.palette.tokens.color.brand100 },
  },
}));

export const TableScroll = styled(TableSurface)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.tokens.color.n300,
    borderRadius: '8px',
  },
}));

export const StyledTable = styled(Table)(() => ({
  minWidth: '1100px',
  '& thead th': {
    whiteSpace: 'nowrap',
  },
  '& tbody td': {
    whiteSpace: 'nowrap',
  },
}));

export const HeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'sortable',
})<{ sortable?: boolean }>(({ sortable, theme }) => ({
  cursor: sortable ? 'pointer' : 'default',
  userSelect: 'none',
  '&:hover': sortable ? { color: theme.palette.text.primary } : undefined,
  '&:focus-visible': {
    outline: 'none',
    boxShadow: `inset ${theme.palette.tokens.color.focusRing}`,
  },
  '& .sort-icon': {
    fontSize: '16px',
    verticalAlign: 'middle',
    marginLeft: '4px',
    color: theme.palette.text.secondary,
  },
  '&.active': {
    color: theme.palette.text.primary,
  },
}));

export const BodyRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  height: '48px',
  '&:hover': {
    backgroundColor: theme.palette.tokens.color.surfaceHover,
  },
  '&:focus-visible': {
    outline: 'none',
    backgroundColor: theme.palette.tokens.color.surfaceHover,
  },
  '&:last-of-type td': {
    borderBottom: 0,
  },
  '& a': {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    fontWeight: theme.palette.tokens.font.weightMedium,
  },
}));

export const MutedCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const ClientCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.palette.tokens.space[3],
  minWidth: 0,
}));

export const ClientAvatar = styled('span')(({ theme }) => {
  const { color, font } = theme.palette.tokens;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: color.n100,
    color: color.textSecondary,
    fontSize: '10px',
    fontWeight: font.weightMedium,
    letterSpacing: '0.02em',
    flexShrink: 0,
  };
});

export const ClientName = styled('span')(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '220px',
  color: theme.palette.text.primary,
}));

export const Toolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.palette.tokens.space[3],
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

export const LoadMoreRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.palette.tokens.space[3],
  padding: theme.palette.tokens.space[4],
  borderTop: `1px solid ${theme.palette.divider}`,
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
}));

export const DeadlineCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.palette.tokens.space[2],
}));

export const ControlCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  fontVariantNumeric: 'tabular-nums lining-nums',
  color: theme.palette.text.primary,
}));

export const ControlSecondary = styled('span')(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));
